import styled from 'styled-components';
import { addAlpha } from '@algoan/deep-dive/theme';
import React from 'react';
import { TickIcon } from '@algoan/deep-dive/icons/tick-icon';
import { LineIcon } from '@algoan/deep-dive/icons/line-icon';
import { ComponentProps } from '@algoan/deep-dive/model';

export enum CheckedState {
  TRUE = 'TRUE',
  FALSE = 'FALSE',
  INTERMEDIATE = 'INTERMEDIATE',
}

export enum CheckboxSize {
  SMALL = 'SMALL',
  LARGE = 'LARGE',
}

interface PartialCheckboxProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size' | 'onChange' | 'checked'> {
  /** A value to dictate whether the checkbox is checked, checked intermediate or unchecked */
  checked: CheckedState | boolean;
  /** A function that is fired upon click or tap of the checkbox. This should be tied to
   * a method to mutate a state value. */
  onChange: (value: boolean) => void;
  /** Size of the checkbox, defaults to small */
  size?: CheckboxSize;
  /** Disables the input, such that no user interaction can be performed. */
  disabled?: boolean;
}

type IdLabelProps =
  | {
      /**  Optional label that displays to the right of the checkbox */
      label: string;
      /** Unique ID which is required when a label is added  */
      id: string;
    }
  | { label?: never; id?: string };

export type CheckboxProps = PartialCheckboxProps & ComponentProps & IdLabelProps;

export const Checkbox = ({
  checked,
  onChange,
  disabled = false,
  size = CheckboxSize.SMALL,
  label,
  id,
  'data-testid': dataTestId,
  className,
  ...props
}: CheckboxProps): JSX.Element => {
  return (
    <S.Container className={className} data-testid={dataTestId ?? 'checkbox'}>
      <S.HiddenCheckbox
        id={id}
        type="checkbox"
        checked={checked === CheckedState.TRUE || checked === CheckedState.INTERMEDIATE || checked === true}
        onChange={() => (checked === CheckedState.FALSE || checked === false ? onChange(true) : onChange(!checked))}
        onClick={(event) => event.stopPropagation()}
        disabled={disabled}
        checkboxSize={size}
        {...props}
      />
      <CheckboxStyles.CheckboxContainer disabled={disabled} size={size}>
        {checked === CheckedState.INTERMEDIATE ? (
          <CheckboxStyles.IntermediateCheckIcon size={size} />
        ) : (
          <CheckboxStyles.CheckmarkIcon size={size} />
        )}
      </CheckboxStyles.CheckboxContainer>
      <CheckboxStyles.Label htmlFor={id} disabled={disabled}>
        {label}
      </CheckboxStyles.Label>
    </S.Container>
  );
};

export const CheckboxStyles = {
  CheckboxContainer: styled.div<{ disabled: boolean; size: CheckboxSize }>`
    display: flex;
    justify-content: center;
    align-items: center;
    height: ${(props) => (props.size === CheckboxSize.LARGE ? '24px' : '16px')};
    width: ${(props) => (props.size === CheckboxSize.LARGE ? '24px' : '16px')};
    border-radius: 4px;
    border: 1px solid ${(props) => (props.disabled ? addAlpha(props.theme.softGrey, 0.4) : props.theme.softGrey)};
    background: ${(props) => props.theme.white};
    cursor: pointer;
  `,
  CheckmarkIcon: styled(TickIcon)<{ size: CheckboxSize }>`
    width: ${(props) => (props.size === CheckboxSize.LARGE ? '15px' : '10px')};
    height: ${(props) => (props.size === CheckboxSize.LARGE ? '12px' : '8px')};
    transform: scale(0);
  `,
  IntermediateCheckIcon: styled(LineIcon)<{ size: CheckboxSize }>`
    display: flex;
    justify-content: center;
    align-items: center;
    width: ${(props) => (props.size === CheckboxSize.LARGE ? '15px' : '10px')};
    height: ${(props) => (props.size === CheckboxSize.LARGE ? '12px' : '8px')};
  `,
  Label: styled.label<{ disabled: boolean }>`
    padding-left: 10px;
    margin: auto 0;
    font-size: 14px;
    cursor: pointer;
    color: ${(props) => (props.disabled ? addAlpha(props.theme.middleGrey, 0.4) : props.theme.algoanBlue)};
  `,
};

const S = {
  Container: styled.div`
    position: relative;
    flex-direction: row;
    display: flex;
    height: fit-content;
  `,
  HiddenCheckbox: styled.input<{ checkboxSize: CheckboxSize }>`
    display: flex;
    justify-content: center;
    align-items: center;
    height: ${(props) => (props.checkboxSize === CheckboxSize.LARGE ? '24px' : '16px')};
    width: ${(props) => (props.checkboxSize === CheckboxSize.LARGE ? '24px' : '16px')};
    border-radius: 4px;
    border: 1px solid ${(props) => (props.disabled ? addAlpha(props.theme.softGrey, 0.4) : props.theme.softGrey)};
    background: ${(props) => props.theme.white};
    cursor: pointer;
    margin: auto ${(props) => (props.checkboxSize === CheckboxSize.LARGE ? '-25px' : '-18px')} auto 0;
    z-index: 50;
    opacity: 0;

    &:checked ~ ${CheckboxStyles.CheckboxContainer} {
      border: 1px solid ${(props) => (props.disabled ? addAlpha(props.theme.softGrey, 0.4) : props.theme.bluePeps)};
      background-color: ${(props) => (props.disabled ? addAlpha(props.theme.softGrey, 0.4) : props.theme.bluePeps)};

      &:hover {
        border-color: ${(props) => (props.disabled ? addAlpha(props.theme.softGrey, 0.4) : props.theme.bluePeps)};
      }
    }

    &:hover ~ ${CheckboxStyles.CheckboxContainer} {
      border-color: ${(props) => (props.disabled ? addAlpha(props.theme.softGrey, 0.4) : props.theme.middleGrey)};
    }

    &:active ~ ${CheckboxStyles.CheckboxContainer} {
      transform: scale(0.95);
      transition-duration: 0.2s;
    }

    &:checked ~ ${CheckboxStyles.CheckboxContainer} {
      ${CheckboxStyles.CheckmarkIcon} {
        transform: scale(1);
        transition-duration: 0.1s;
      }
    }
  `,
};
